import type { PortalProblemRecord } from "./portal-runtime";

export type RecordSort = "updated" | "claims" | "severity" | "code";
export function selectProblemRecords(
  records: readonly PortalProblemRecord[],
  query: string,
  status: string,
  severity: string,
  sort: RecordSort,
) {
  const search = query.trim().toLowerCase();
  const severityOrder = { UNASSESSED: 0, LOW: 1, MEDIUM: 2, HIGH: 3, CRITICAL: 4 };
  return records
    .filter(
      (record) =>
        (!search ||
          [record.displayId, record.title, record.summary, record.ownerUserId, record.sourceSummary]
            .filter(Boolean)
            .join(" ")
            .toLowerCase()
            .includes(search)) &&
        (status === "ALL" || record.status === status) &&
        (severity === "ALL" || record.severity === severity),
    )
    .slice()
    .sort((a, b) => {
      if (sort === "claims")
        return (
          (b.claimCount ?? -1) - (a.claimCount ?? -1) || a.displayId.localeCompare(b.displayId)
        );
      if (sort === "severity")
        return (
          severityOrder[b.severity] - severityOrder[a.severity] ||
          a.displayId.localeCompare(b.displayId)
        );
      if (sort === "code") return a.displayId.localeCompare(b.displayId);
      return b.updatedAt.localeCompare(a.updatedAt) || a.displayId.localeCompare(b.displayId);
    });
}

export function formatRecordCost(record: Pick<PortalProblemRecord, "impact">): string {
  if (!record.impact) return "Unavailable";
  const value = Number(record.impact.amountMinor);
  if (!Number.isSafeInteger(value))
    return `${record.impact.currencyCode} ${record.impact.amountMinor} minor units`;
  const formatter = new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: record.impact.currencyCode,
  });
  const digits = formatter.resolvedOptions().maximumFractionDigits ?? 2;
  const scale = 10n ** BigInt(digits);
  const minor = BigInt(record.impact.amountMinor);
  const whole = minor / scale;
  const remainder = minor < 0n ? -(minor % scale) : minor % scale;
  // Currency precision comes from Intl; integer arithmetic keeps every minor
  // unit exact, including amounts near Number.MAX_SAFE_INTEGER.
  const fraction = digits
    ? new Intl.NumberFormat(undefined, {
        useGrouping: false,
        minimumIntegerDigits: digits,
        maximumFractionDigits: 0,
      }).format(remainder)
    : "";
  return formatter
    .formatToParts(minor < 0n && whole === 0n ? -0 : whole)
    .map((part) => (part.type === "fraction" ? fraction : part.value))
    .join("");
}

function csvCell(value: string | number) {
  const text = String(value);
  // Neutralize spreadsheet formulas, including values preceded by whitespace.
  const safe = /^[\s]*[=+@-]/.test(text) || /^[\t\r]/.test(text) ? `'${text}` : text;
  return `"${safe.replaceAll('"', '""')}"`;
}

export function problemRecordsCsv(records: readonly PortalProblemRecord[]) {
  const header = [
    "Record",
    "Title",
    "Status",
    "Severity",
    "Linked claims",
    "Source cost minor units",
    "Currency",
    "Owner",
    "Updated",
    "Classification",
    "Data cutoff",
    "Freshness",
    "Quality",
    "Reconciliation",
    "Source",
  ];
  const rows = records.map((r) => [
    r.displayId,
    r.title,
    r.status,
    r.severity,
    r.claimCount ?? "",
    r.impact?.amountMinor ?? "",
    r.impact?.currencyCode ?? "",
    r.ownerUserId ?? "",
    r.updatedAt,
    r.classification ?? "",
    r.dataCutoffAt ?? "",
    r.freshness ?? "",
    r.quality ?? "",
    r.reconciliation ?? "",
    r.sourceSummary ?? "",
  ]);
  return "\uFEFF" + [header, ...rows].map((row) => row.map(csvCell).join(",")).join("\r\n");
}
